import { Controller } from "@hotwired/stimulus"
import { Offcanvas, Dropdown } from "bootstrap"

// 会話と下書きを保ち、生成中だけ更新する。
export default class extends Controller {
  static targets = ["sheet", "content", "input", "submit", "error"]
  static values = { showUrl: String, messageUrl: String }

  connect() {
    this.conversationId = ""
    this.history = "active"
    this.historyPage = 0
    this.requestVersion = 0
    this.drafts = new Map()
    this.isOpen = false
    this.busy = false
    this.loaded = false
    this.onHidden = () => {
      this.isOpen = false
      this.stopPolling()
      this.refreshController?.abort()
      clearTimeout(this.focusTimer)
    }
    this.onShown = () => this.resizeInput()
    this.sheetTarget.addEventListener("hidden.bs.offcanvas", this.onHidden)
    this.sheetTarget.addEventListener("shown.bs.offcanvas", this.onShown)
    this.inputResizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === this.inputWidth) return
      this.inputWidth = entry.contentRect.width
      this.resizeInput()
    })
    this.inputResizeObserver.observe(this.inputTarget)
    this.resizeInput()
  }

  disconnect() {
    this.isOpen = false
    this.requestVersion += 1
    this.stopPolling()
    this.refreshController?.abort()
    clearTimeout(this.focusTimer)
    this.inputResizeObserver.disconnect()
    this.disposeHistory()
    this.sheetTarget.removeEventListener("hidden.bs.offcanvas", this.onHidden)
    this.sheetTarget.removeEventListener("shown.bs.offcanvas", this.onShown)
  }

  open() {
    this.isOpen = true
    this.offcanvas ||= Offcanvas.getOrCreateInstance(this.sheetTarget)
    this.offcanvas.show()
    if (!this.busy) this.refresh()
    this.focusTimer = setTimeout(() => {
      if (this.isOpen) this.inputTarget.focus()
    }, 350)
  }

  async select(event) {
    if (this.busy) return
    this.closeHistory()
    await this.loadConversation(String(event.params.conversationId))
  }

  async reset() {
    if (this.busy) return
    this.closeHistory()
    await this.loadConversation("new")
    this.inputTarget.focus()
  }

  async loadConversation(id) {
    const previousId = this.conversationId
    this.switchConversation(id)
    if (await this.refresh() === false && this.conversationId === id) {
      this.switchConversation(previousId)
      this.syncControls()
      this.schedulePollIfPending()
    }
  }

  switchConversation(id) {
    this.drafts.set(this.conversationId, this.inputTarget.value)
    this.conversationId = id
    this.inputTarget.value = this.drafts.get(id) || ""
    this.resizeInput()
    this.stopPolling()
  }

  showHistory(event) {
    if (this.busy) return
    this.history = event.currentTarget.dataset.history
    this.historyPage = Math.max(0, Number(event.currentTarget.dataset.page) || 0)
    this.refresh()
  }

  async refresh({ allowBusy = false } = {}) {
    if ((this.busy && !allowBusy) || !this.isOpen) return
    const version = ++this.requestVersion
    this.refreshController?.abort()
    this.refreshController = new AbortController()
    const url = this.panelUrl()
    try {
      const response = await this.request(url, { headers: { Accept: "text/html" } }, this.refreshController)
      return await this.renderResponse(response, version)
    } catch (error) {
      if (version !== this.requestVersion || !this.isOpen) return
      this.showError("会話を更新できませんでした。再度開くと読み直せます。下書きは保持しています。")
      this.schedulePollIfPending()
      return false
    }
  }

  panelUrl() {
    const url = new URL(this.showUrlValue, window.location.origin)
    if (this.conversationId) url.searchParams.set("conversation_id", this.conversationId)
    url.searchParams.set("history", this.history)
    url.searchParams.set("history_page", this.historyPage)
    return url
  }

  async request(url, options, controller = new AbortController()) {
    const timer = setTimeout(() => controller.abort(), 30000)
    try {
      return await fetch(url, { ...options, signal: controller.signal })
    } finally {
      clearTimeout(timer)
    }
  }

  async send(event) {
    event.preventDefault()
    const original = this.inputTarget.value
    const content = original.trim()
    if (!content || !this.loaded || this.busy || this.pending() || this.archived()) return

    this.beginMutation()
    const version = this.requestVersion
    const body = new FormData()
    body.append("content", content)
    body.append("conversation_id", this.conversationId)
    body.append("history", this.history)
    body.append("history_page", this.historyPage)
    try {
      const response = await this.request(this.messageUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "text/html" },
        body
      })
      if (await this.renderResponse(response, version, true)) {
        if (this.inputTarget.value === original) this.inputTarget.value = ""
        this.drafts.delete(this.conversationId)
      }
    } catch {
      if (version === this.requestVersion) this.showError("送信結果を確認できませんでした。履歴を確認してから再送してください。下書きは保持しています。")
    } finally {
      this.endMutation()
      this.resizeInput()
    }
  }

  async archiveConversation(event) {
    if (this.busy) return
    const button = event.currentTarget
    const restoring = button.dataset.restore === "true"
    const targetId = button.dataset.conversationId
    const currentId = this.conversationId
    this.beginMutation()
    const version = this.requestVersion
    const body = new FormData()
    body.append("conversation_id", targetId)
    body.append("history", this.history)
    body.append("history_page", this.historyPage)
    try {
      const response = await this.request(button.dataset.url, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "text/html" },
        body
      })
      // 別の履歴を整理したときも、開いている会話は保つ。
      if (!response.ok) {
        await this.renderResponse(response, version)
      } else if (!restoring && targetId !== currentId) {
        await this.refresh({ allowBusy: true })
      } else {
        this.drafts.set(currentId, this.inputTarget.value)
        if (await this.renderResponse(response, version, true)) {
          this.inputTarget.value = this.drafts.get(this.conversationId) || ""
        }
      }
    } catch {
      if (version === this.requestVersion) this.showError("履歴を更新できませんでした。会話と下書きは保持しています。")
    } finally {
      this.endMutation()
      this.resizeInput()
    }
  }

  beginMutation() {
    this.busy = true
    this.requestVersion += 1
    this.refreshController?.abort()
    this.stopPolling()
    this.syncControls()
  }

  endMutation() {
    this.busy = false
    this.syncControls()
    this.schedulePollIfPending()
  }

  async renderResponse(response, version, forceBottom = false) {
    const html = await response.text()
    if (version !== this.requestVersion) return false
    if (!response.ok) {
      this.showError(response.status === 409 ? html : "会話を読み込めませんでした。下書きは保持しています。")
      this.schedulePollIfPending()
      return false
    }

    const template = document.createElement("template")
    template.innerHTML = html
    if (!template.content.querySelector(".ai-chat__panel")) {
      this.showError("会話を読み込めませんでした。ログイン状態を確認してください。下書きは保持しています。")
      return false
    }

    const thread = this.contentTarget.querySelector(".ai-chat__thread")
    const previousId = this.panel()?.dataset.conversationId
    const scrollTop = thread?.scrollTop || 0
    const atBottom = !thread || thread.scrollHeight - scrollTop - thread.clientHeight < 60
    const openDetails = [...this.contentTarget.querySelectorAll("details[open][data-ai-chat-details-id]")]
      .map((detail) => detail.dataset.aiChatDetailsId)
    const historyOpen = this.historyToggle()?.getAttribute("aria-expanded") === "true"
    const historyScroll = this.contentTarget.querySelector(".ai-chat__history")?.scrollTop || 0

    this.disposeHistory()
    this.contentTarget.innerHTML = html
    this.loaded = true
    const panel = this.panel()
    this.conversationId = panel?.dataset.conversationId || "new"
    this.history = panel?.dataset.history || this.history
    this.historyPage = Number(panel?.dataset.historyPage) || 0
    const sameConversation = previousId === this.conversationId
    this.contentTarget.querySelectorAll("details[data-ai-chat-details-id]").forEach((detail) => {
      if (sameConversation && openDetails.includes(detail.dataset.aiChatDetailsId)) detail.open = true
    })
    if (historyOpen && this.isOpen) Dropdown.getOrCreateInstance(this.historyToggle()).show()
    const historyMenu = this.contentTarget.querySelector(".ai-chat__history")
    if (historyMenu) historyMenu.scrollTop = historyScroll
    const nextThread = this.contentTarget.querySelector(".ai-chat__thread")
    if (nextThread) nextThread.scrollTop = forceBottom || !sameConversation || atBottom ? nextThread.scrollHeight : scrollTop
    this.errorTarget.hidden = true
    const wideButton = this.contentTarget.querySelector('[data-action="ai-chat#toggleWide"]')
    if (wideButton) this.syncWideControl(wideButton)
    this.resizeInput()
    this.schedulePollIfPending()
    return true
  }

  syncControls() {
    const blocked = this.busy || this.pending() || this.archived()
    this.submitTarget.disabled = !this.loaded || blocked || !this.inputTarget.value.trim()
    this.inputTarget.disabled = this.archived()
    this.inputTarget.placeholder = this.archived() ? "復元すると会話を続けられます" : "質問・相談を入力"
    this.contentTarget.querySelectorAll("button").forEach((button) => {
      if (button.dataset.bsDismiss === "offcanvas") return
      if (this.busy) {
        if (button.dataset.aiChatPreviouslyDisabled === undefined) button.dataset.aiChatPreviouslyDisabled = String(button.disabled)
        button.disabled = true
      } else if (button.dataset.aiChatPreviouslyDisabled !== undefined) {
        button.disabled = button.dataset.aiChatPreviouslyDisabled === "true"
        delete button.dataset.aiChatPreviouslyDisabled
      }
    })
  }

  historyToggle() {
    return this.contentTarget.querySelector(".ai-chat__history-toggle")
  }

  closeHistory() {
    const toggle = this.historyToggle()
    if (toggle) Dropdown.getInstance(toggle)?.hide()
  }

  disposeHistory() {
    const toggle = this.historyToggle()
    if (toggle) Dropdown.getInstance(toggle)?.dispose()
  }

  panel() {
    return this.contentTarget.querySelector(".ai-chat__panel")
  }

  pending() {
    return this.panel()?.dataset.aiChatPending === "true"
  }

  archived() {
    return this.panel()?.dataset.archived === "true"
  }

  schedulePollIfPending() {
    this.stopPolling()
    if (this.isOpen && !this.busy && this.pending()) {
      this.pollTimer = setTimeout(() => this.refresh(), 3000)
    }
  }

  stopPolling() {
    clearTimeout(this.pollTimer)
    this.pollTimer = null
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  handleKeydown(event) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && !event.isComposing && event.keyCode !== 229) {
      this.send(event)
    }
  }

  resizeInput() {
    const input = this.inputTarget
    if (input.clientWidth) {
      const style = getComputedStyle(input)
      const lineHeight = parseFloat(style.lineHeight)
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
      const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
      const maxHeight = Math.ceil(lineHeight * 5 + padding + border)
      const scrollTop = input.scrollTop
      input.style.height = "auto"
      input.style.overflowY = "hidden"
      const contentHeight = input.scrollHeight + border
      input.style.height = `${Math.min(contentHeight, maxHeight)}px`
      input.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden"
      input.scrollTop = scrollTop
    }
    this.syncControls()
  }

  toggleWide(event) {
    this.sheetTarget.classList.toggle("ai-chat-sheet--wide")
    this.syncWideControl(event.currentTarget)
  }

  syncWideControl(button) {
    const wide = this.sheetTarget.classList.contains("ai-chat-sheet--wide")
    button.setAttribute("aria-pressed", String(wide))
    button.setAttribute("aria-label", wide ? "表示幅を戻す" : "表示を広げる")
    button.title = wide ? "表示幅を戻す" : "表示を広げる"
  }

  async copyAnswer(event) {
    const button = event.currentTarget
    const source = button.closest(".ai-chat__msg").querySelector("[data-ai-chat-copy-source]")
    try {
      await navigator.clipboard.writeText(source.value)
      button.title = "コピーしました"
    } catch {
      this.showError("コピーできませんでした。回答の文章を選択してコピーしてください。")
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
